import { Clock } from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { ROUTES } from "@/router";
import type { MemoPriceSnapshot } from "@/types/proto/api/v1/memo_service_pb";

interface MemoPriceSnapshotBadgesProps {
  priceSnapshots: MemoPriceSnapshot[];
  variant?: "desktop" | "mobile";
}

const formatPrice = (price: number): string => {
  return price.toFixed(2);
};

const formatTimestamp = (timestamp: number): string => {
  const date = new Date(timestamp * 1000);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const toNumber = (val: unknown): number => {
  if (val === null || val === undefined) return 0;
  if (typeof val === "bigint") return Number(val);
  if (typeof val === "number") return val;
  return Number(val) || 0;
};

const MemoPriceSnapshotBadges: React.FC<MemoPriceSnapshotBadgesProps> = ({ priceSnapshots, variant = "desktop" }) => {
  const isMobile = variant === "mobile";

  const enhancedSnapshots = useMemo(() => {
    return priceSnapshots.map((snap) => ({
      ...snap,
      price: toNumber(snap.price),
      timestamp: toNumber(snap.timestamp),
    }));
  }, [priceSnapshots]);

  if (enhancedSnapshots.length === 0) {
    return null;
  }

  return (
    <div className={cn("shrink-0", isMobile ? "flex flex-row flex-wrap gap-1.5 w-full" : "flex flex-col gap-1 ml-2")}>
      {enhancedSnapshots.map((snapshot) => (
        <TooltipProvider key={snapshot.id} delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="outline"
                className={cn(
                  "cursor-pointer hover:bg-accent transition-colors",
                  "text-muted-foreground",
                  isMobile ? "text-xs px-2 py-0.5 whitespace-nowrap" : "text-sm px-2.5 py-1",
                )}
                asChild
              >
                <Link to={ROUTES.TICKER_DETAIL.replace(":symbol", snapshot.tickerSymbol)} onClick={(e) => e.stopPropagation()}>
                  <Clock
                    className={cn("text-muted-foreground", isMobile ? "w-3 h-3 mr-1" : "w-3.5 h-3.5 mr-1.5")}
                    aria-label="Historical price snapshot"
                  />
                  <span className="font-medium">{snapshot.tickerSymbol}</span>
                  <span className={cn("ml-1", isMobile ? "text-[10px]" : "text-xs")}>${formatPrice(snapshot.price)}</span>
                </Link>
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="top" align="center">
              <div className="flex flex-col gap-1">
                <p className="font-medium text-sm">Historical price when memo was created</p>
                <p className="text-xs text-muted-foreground">{formatTimestamp(snapshot.timestamp)}</p>
                <p className="text-xs text-primary pt-1">Click to see current price →</p>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ))}
    </div>
  );
};

export default MemoPriceSnapshotBadges;
